"use client";
import { useState, useEffect, useId } from "react";
import PropTypes from "prop-types";

import { useSelector, useDispatch } from "react-redux";

import { setInitialLocation, setFreeText, setInitialParams, setLocalities } from "@/src/store/filter";
import { urlMaker } from "services/url-maker";

import { setMapOrListingFinalPath } from "src/helpers/map/commons";
import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";

import InnerBox from "../inner-box";
import Button from "components/common/button";
import CityDropdown from "./location-filters/city";
import DistrictsDropdown from "./location-filters/districts";
import TownsDropdown from "./location-filters/towns";
import FreeTextInput from "./freetext";
import MultiSelectFilter from "./params-filter/multi-select";
import AdTypeFilter from "./params-filter/ad-type-filter";
import SingleSelectFilter from "./params-filter/single-select";
import PriceInput from "./params-filter/range-select/price-input";
import MinMaxInput from "./params-filter/range-select/min-max-input";
import MinInput from "./params-filter/range-select/min-input";
import CompaniesDropDown from "./companies-filters";

import styles from "./styles.module.css";

const StaticFilterItems = ({ criteria, screenType, filters }) => {
  const [adTypeInformation, setAdTypeInformation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [handledLocality, setHandledLocality] = useState(false);
  const id = useId();

  const dispatch = useDispatch();
  
  const {
    rangeselectitems: rangeReduxItems,
    multiselectitems: multiReduxItems,
    singleselectitems: singleReduxItems,
    adtypesitems: adTypeReduxItems,
    freetext: freetextRedux,
    city: cityRedux,
    districts: districtRedux,
    towns: townsRedux,
    localities: localitiesRedux,
    companies
  } = useSelector((state) => state.filter);

  const isProject = criteria?.params.some((param) => param?.ownerType === 'PROJECTS');

  const handleFilterButton = async () => {
    Ga4WithEventParameters({
      eventName: "filtre",
      eventParameters: { label: "click||uygula" },
    });
    setLoading(true);
    const hasLocations = criteria?.location || cityRedux || districtRedux || townsRedux || localitiesRedux;
    let newCriteria = {
      ...criteria,
      params: [...rangeReduxItems, ...multiReduxItems, ...singleReduxItems, ...adTypeReduxItems],
      freetext: freetextRedux?.value ? freetextRedux : null,
      location: hasLocations
        ? {
            ...criteria?.location,
            city: cityRedux,
            districts: districtRedux,
            towns: townsRedux,
            localities: localitiesRedux,
          }
        : null,
      // reset pagination for all change filter change actions
      pagination: {
        key: "pagination",
        page: 0,
        pageSize: 30,
        horizontalPage: false,
      },
    };

    if (isProject) {
      newCriteria = {
        ...newCriteria,
        company: {
          key: 'company_id',
          slug: null,
          value: companies,
        },
      };
    }

    const res = await urlMaker(newCriteria, "default");
    window.location.href = setMapOrListingFinalPath({ res, screenType });
  };

  useEffect(() => {
    dispatch(setFreeText(criteria?.freetext));
    dispatch(setInitialParams(criteria?.params));
    dispatch(setInitialLocation(criteria?.location));
  }, []);

  useEffect(() => {
    if (filters?.result && adTypeInformation.length === 0) {
      filters.result.forEach((filter) => {
        if (filter.key !== "virtual_decoration") {
          if (["checkbox", "exists"].includes(filter.type)) {
            setAdTypeInformation((prev) => [...prev, filter]);
          }
        }
      });
    }
  }, [filters]);

  useEffect(() => {
    dispatch(setLocalities(criteria.location?.localities));
    setHandledLocality(true);
  }, [criteria.location?.localities && !localitiesRedux.length && !handledLocality]);

  return (
    <div className={styles.wrapper}>
      <InnerBox title="Adres">
        <CityDropdown />
        <DistrictsDropdown />
        <TownsDropdown />
      </InnerBox>
      <hr className={styles.lineMargin} />
      <InnerBox title="Arama Kelimesi">
        <FreeTextInput />
      </InnerBox>
      {isProject &&
      <>
       <hr className={styles.lineMargin} />
         <CompaniesDropDown criteria={criteria} />
      </>
      }
      <hr className={styles.line} />
      {adTypeInformation?.length > 0 && (
        <InnerBox title={isProject ? "Proje Tipi" : "İlan Tipi"}>
          <AdTypeFilter information={adTypeInformation} />
        </InnerBox>
      )}
      <hr className={styles.line} />
      {filters?.result?.map((filter, index) => {
        return (
          <div key={`${id}-${index}`}>
            {filter?.type === "price" && (
              <>
                <InnerBox title={filter?.name}>
                  <PriceInput filter={filter} />
                </InnerBox>
                <hr className={styles.lineMargin} />
              </>
            )}
            {filter?.type === "min_max" && (
              <>
                <InnerBox title={filter?.name}>
                  <MinMaxInput filter={filter} filterKey={filter?.key} />
                </InnerBox>
                <hr className={styles.lineMargin} />
              </>
            )}
            {filter?.type === "min" && (
              <>
                <InnerBox title={filter?.name}>
                  <MinInput filter={filter} filterKey={filter?.key} />
                </InnerBox>
                <hr className={styles.lineMargin} />
              </>
            )}
            {filter?.type === "multi_select" && <MultiSelectFilter filter={filter} />}
            {filter?.type === "single_select" && (
              <>
                <InnerBox isDropdownView isHidden={!filter?.hidden} title={filter?.name}>
                  <SingleSelectFilter filter={filter} />
                </InnerBox>
                <hr className={styles.line} />
              </>
            )}
          </div>
        );
      })}
      <div className={styles.filterButtonWrap}>
        <div className={styles.filterButton}>
          <Button size="M" isLoading={loading} onClick={handleFilterButton}>
            Filtrele
          </Button>
        </div>
      </div>
    </div>
  );
};

StaticFilterItems.propTypes = {
  screenType: PropTypes.string,
  criteria: PropTypes.object,
  filters: PropTypes.object,
};

export default StaticFilterItems;
